import { useSearchParams } from 'react-router-dom';
import { useEffect, useMemo } from 'react';
import { Tag, Typography } from 'antd';
import { ResourceList } from '@/components/ResourceList';
import { listTransactions, type DiamondTransaction } from '@/api/diamonds';
import { formatDateTime, truncate } from '@/utils/format';

export function DiamondsListPage() {
  // Support deep links from User Detail: /diamonds?user_id=usr_...
  const [params] = useSearchParams();
  const initialUserId = params.get('user_id') ?? undefined;

  // The ResourceList stores filter state internally; we surface the deep-link
  // user_id via a banner so the operator knows the list is pre-filtered.
  const banner = useMemo(() => {
    if (!initialUserId) return null;
    return (
      <Typography.Text type="secondary">
        当前已按 user_id 过滤：<Tag>{truncate(initialUserId, 18)}</Tag>
      </Typography.Text>
    );
  }, [initialUserId]);

  // Hack: when user opens /diamonds?user_id=X we want the first fetch to
  // include that filter. We do that by closing over initialUserId in the
  // fetcher. Subsequent filter changes work normally through the form.
  useEffect(() => {
    // no-op effect — purely to silence eslint if needed.
  }, []);

  return (
    <ResourceList<DiamondTransaction>
      title="钻石流水"
      description={banner ?? '只读流水；正向 delta = credit，负向 = spend；带 ref_id 链接到来源任务/订单。'}
      rowKey="id"
      pagination="cursor"
      filters={[
        { key: 'user_id', label: '用户 ID', type: 'text', placeholder: 'usr_...' },
        { key: 'app_id', label: 'App ID', type: 'text', placeholder: '数字' },
        {
          key: 'direction',
          label: '方向',
          type: 'select',
          options: [
            { value: 'in', label: '充值 / 奖励 (+)' },
            { value: 'out', label: '消耗 (-)' },
          ],
        },
        {
          key: 'reason',
          label: '原因',
          type: 'select',
          options: [
            { value: 'topup', label: 'topup' },
            { value: 'generation', label: 'generation' },
            { value: 'admin_grant', label: 'admin_grant' },
            { value: 'admin_revoke', label: 'admin_revoke' },
            { value: 'share_bonus', label: 'share_bonus' },
            { value: 'invite_reward', label: 'invite_reward' },
            { value: 'refund', label: 'refund' },
          ],
        },
      ]}
      fetcher={({ filters, pageSize, cursor }) => {
        const f = { ...filters } as Record<string, unknown>;
        if (typeof f.app_id === 'string') f.app_id = Number(f.app_id);
        // Apply deep-link only on the very first fetch (cursor undefined +
        // no explicit form value).
        if (!f.user_id && initialUserId && !cursor) {
          f.user_id = initialUserId;
        }
        return listTransactions({ ...f, cursor, limit: pageSize } as never);
      }}
      columns={[
        { title: 'ID', dataIndex: 'id', render: (v) => truncate(v, 14) },
        { title: '用户', dataIndex: 'user_id', render: (v) => truncate(v, 12) },
        {
          title: '变动',
          dataIndex: 'delta',
          align: 'right',
          render: (v: number) => (
            <Typography.Text style={{ color: v > 0 ? '#16a34a' : '#dc2626' }}>
              {v > 0 ? `+${v}` : v}
            </Typography.Text>
          ),
        },
        { title: '余额', dataIndex: 'balance_after', align: 'right' },
        { title: '原因', dataIndex: 'reason', render: (v) => <Tag>{v}</Tag> },
        {
          title: 'App',
          dataIndex: 'app_slug',
          render: (v, row) => (v ? <Tag color="blue">{v}</Tag> : `app#${row.app_id ?? '-'}`),
        },
        { title: 'ref_id', dataIndex: 'ref_id', render: (v) => (v ? truncate(v, 14) : '-') },
        { title: '时间', dataIndex: 'created_at', render: (v) => formatDateTime(v) },
      ]}
    />
  );
}
